/**
 * Who the letter is for: ONE field you type into to search, with the list underneath (David,
 * Oct 13 2026 — the search box above a separate dropdown was awkward). Built here rather than on the
 * DS Autocomplete, which points its input at an EMPTY label of its own and so has no accessible name
 * (see CLAUDE.md); this one is the design-system Input, named by its own label, with the ARIA
 * combobox pattern on top. The field shows the chosen person while idle and the search while typing,
 * and only a choice from the list changes the value — the page can never show one person and send
 * another. Direct reports come first, under their own heading.
 */
import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { Input } from '@finn-auto/design-system/atoms/input';
import type { FormPerson } from '../../types/referenceLetterForm';
import { cn } from '../../../../shared';
import { contractLine } from './requestForm';
import { ERROR_TEXT } from './RequestFields';

const LISTBOX_ID = 'employee-listbox';
// Rendered at most; the BP group reaches all of FINN, and typing narrows it anyway.
const MAX_SHOWN = 100;
const optionId = (id: string) => `employee-option-${id}`;

/** Every typed word must appear in the name or the contract line, so "lena work" finds Lena the working student. */
function matching(people: FormPerson[], query: string): FormPerson[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return people;
  return people.filter((p) => {
    const hay = `${p.name ?? ''} ${contractLine(p)}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/**
 * @param people whom the field offers, direct reports first (`candidatesFor`)
 * @param othersLabel the heading over everyone who is not a direct report
 * @param disabledHint shown in the field while there is nothing to choose from yet
 */
export function PersonCombobox({
  people, value, error, othersLabel, disabledHint, emptyHint, onChange,
}: {
  people: FormPerson[];
  value: string | null;
  error?: string;
  othersLabel: string;
  disabledHint?: string;
  emptyHint: string;
  onChange: (id: string) => void;
}) {
  // null: not searching, so the field shows the chosen person and the list shows everyone.
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const chosen = people.find((p) => p.id === value) ?? null;
  const found = useMemo(() => matching(people, query ?? ''), [people, query]);
  // `people` comes direct reports first, so the cut keeps them; `flat` is the arrow keys' order.
  const flat = useMemo(() => found.slice(0, MAX_SHOWN), [found]);
  const groups = useMemo(() => {
    const indexed = flat.map((p, index) => ({ p, index }));
    const direct = indexed.filter(({ p }) => p.direct);
    const others = indexed.filter(({ p }) => !p.direct);
    // A heading only when it tells two groups apart.
    if (!direct.length) return [{ label: null, people: others }];
    return [{ label: 'Your direct reports', people: direct }, { label: othersLabel, people: others }].filter((g) => g.people.length);
  }, [flat, othersLabel]);
  const activeId = open && flat[active] ? optionId(flat[active].id) : undefined;

  // The highlighted option stays in view while the arrow keys move past the list's edge.
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  /** Opens the list with the chosen person highlighted, or the first one. */
  const openList = () => {
    setOpen(true);
    setActive(Math.max(0, flat.findIndex((p) => p.id === value)));
  };
  /** Closes the list and puts the chosen person back in the field. */
  const close = () => {
    setOpen(false);
    setQuery(null);
  };
  /** Takes a person from the list. */
  const choose = (p: FormPerson) => {
    onChange(p.id);
    close();
  };
  /** Arrows move, Enter takes the highlighted person, Escape gives up the search. */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openList();
      setActive((i) => (e.key === 'ArrowDown' ? Math.min(i + 1, flat.length - 1) : Math.max(i - 1, 0)));
    } else if (e.key === 'Enter' && open && flat[active]) {
      e.preventDefault();
      choose(flat[active]);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };

  const disabled = Boolean(disabledHint);
  return (
    <div className="relative">
      <Input
        id="employee"
        className="rounded"
        label="Employee"
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'employee-error employee-hint' : 'employee-hint'}
        autoComplete="off"
        disabled={disabled}
        value={query ?? chosen?.name ?? ''}
        error={Boolean(error)}
        onFocus={openList}
        onClick={() => !open && openList()}
        onBlur={close}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {/* Kept in the DOM while closed: the input's aria-controls must point at something. */}
      <div
        // A press on the list must not blur the field, or the list closes before the click lands.
        onMouseDown={(e) => e.preventDefault()}
        className={cn('absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded border border-solid border-pearl bg-white py-1 shadow-lg', !open && 'hidden')}
      >
        <div id={LISTBOX_ID} role="listbox" aria-label="Employee">
          {groups.map((g) => (
            <div key={g.label ?? 'all'} role="group" aria-label={g.label ?? undefined}>
              {g.label && <div role="presentation" className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-zinc-600">{g.label}</div>}
              {g.people.map(({ p, index: i }) => {
                return (
                  <div
                    key={p.id}
                    id={optionId(p.id)}
                    role="option"
                    aria-selected={p.id === value}
                    onClick={() => choose(p)}
                    onMouseEnter={() => setActive(i)}
                    className={cn('cursor-pointer px-4 py-2', i === active && 'bg-cotton', p.id === value && 'font-semibold')}
                  >
                    <span className="block text-sm text-zinc-900">{p.name || 'Unnamed'}</span>
                    {/* The separator is for the option's accessible name, which reads as one line. */}
                    <span className="sr-only"> · </span>
                    <span className="block text-xs text-zinc-600">{contractLine(p)}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {!flat.length && <p className="px-4 py-2 text-sm text-zinc-600">{query ? 'Nobody matches.' : emptyHint}</p>}
        {found.length > flat.length && <p className="px-4 py-2 text-xs text-zinc-600">{found.length - flat.length} more — keep typing to narrow the list.</p>}
      </div>
      {/* The chosen person as a card, so who the letter is for is unmistakable; it also describes the field. */}
      {chosen && !disabledHint ? (
        <div id="employee-hint" data-testid="employee-card" className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded border border-solid border-pearl bg-white px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-zinc-900">{chosen.name || 'Unnamed'}</p>
            <p className="text-sm text-zinc-700">{contractLine(chosen)}</p>
          </div>
          {chosen.direct && <Badge variant="blue">Your direct report</Badge>}
        </div>
      ) : (
        <p id="employee-hint" className="mt-1 text-sm text-zinc-700">{disabledHint ?? 'Type a name to search, or open the list.'}</p>
      )}
      {error && <p id="employee-error" className={`mt-1 ${ERROR_TEXT}`}>{error}</p>}
    </div>
  );
}
